/**
 * Custom Hook: useVehicleData
 * Manages vehicle colors, models extraction from spreadsheet, and year ranges
 */

import { useEffect, useMemo, useState } from "react";
import { VEHICLE_COLORS, YEAR_RANGE } from "../utils/constants/vehicleData";
import FixedAgent from "../utils/api/FixedAgent";
import { showErrorToast } from "../utils/toastFactory";

const normalizeVehicleName = (value) =>
  value
    ?.toString()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]/g, "")
    .trim() || "";

const getSpreadsheetValue = (row, fieldName) => {
  if (!row) return "";

  return (
    row[fieldName] ||
    row[Object.keys(row).find((key) => key.trim() === fieldName)] ||
    ""
  );
};

export const useVehicleData = (
  selectedMakeId,
  makeModelMapping,
  renewData,
  ceiMake,
  niconMake,
  salamMeta,
  agMeta,
  token,
) => {
  const isSalam = renewData.insuranceType === "SALAM";
  const isAg = renewData.insuranceType === "AG";
  const isAgp = renewData.insuranceType === "AGP";
  const isAgFamily = isAg || isAgp;
  const [defaultVehicleColor] = useState(VEHICLE_COLORS);
  const [vehicleModel, setVehicleModel] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const vehicleColor = useMemo(
    () =>
      isSalam
        ? salamMeta?.colors || []
        : isAg
          ? agMeta?.colors || []
          : isAgp
            ? agMeta?.agpColors || []
          : defaultVehicleColor,
    [
      agMeta?.agpColors,
      agMeta?.colors,
      defaultVehicleColor,
      isAg,
      isAgp,
      isSalam,
      salamMeta?.colors,
    ],
  );

  const yearRange = useMemo(
    () =>
      isAgFamily
        ? {
            start: (isAg ? agMeta?.years : agMeta?.agpYears)?.[0]?.id || YEAR_RANGE.start,
            end:
              (isAg ? agMeta?.years : agMeta?.agpYears)?.[
                (isAg ? agMeta?.years : agMeta?.agpYears)?.length - 1
              ]?.id || YEAR_RANGE.end,
            years:
              (isAg ? agMeta?.years : agMeta?.agpYears)
                ?.map((year) => year.id || year.name)
                .filter(Boolean) ||
              Array.from(
                { length: YEAR_RANGE.end - YEAR_RANGE.start + 1 },
                (_, i) => YEAR_RANGE.start + i,
              ),
          }
        : {
            start: YEAR_RANGE.start,
            end: YEAR_RANGE.end,
            years: Array.from(
              { length: YEAR_RANGE.end - YEAR_RANGE.start + 1 },
              (_, i) => YEAR_RANGE.start + i,
            ),
          },
    [agMeta?.agpYears, agMeta?.years, isAg, isAgFamily],
  );

  useEffect(() => {
    if (!isSalam) return;

    console.log("[Salam Vehicle Data] Using Salam metadata", {
      selectedMakeId,
      colorsCount: vehicleColor.length,
      makesCount: salamMeta?.makes?.length || 0,
      sampleMake: salamMeta?.makes?.[0],
      sampleColor: vehicleColor?.[0],
    });
  }, [isSalam, salamMeta, selectedMakeId, vehicleColor]);

  /**
   * Extract models from spreadsheet based on selected make ID
   */
  useEffect(() => {
    const extractModelsFromSpreadsheet = () => {
      if (isAgFamily) {
        setVehicleModel(agMeta?.vehicleMakeModels || []);
        return;
      }

      if (!selectedMakeId) {
        setVehicleModel([]);
        return;
      }

      // Resolve the Make Name from the hardcoded lists
      const makeArray =
        renewData.insuranceType === "NICON"
          ? niconMake
          : isSalam
            ? salamMeta?.makes || []
            : ceiMake;
      const selectedMakeObj = makeArray.find((m) => m.id === selectedMakeId);
      const selectedMakeFromSheet = makeModelMapping.data.find(
        (row) => getSpreadsheetValue(row, "Make ID") === selectedMakeId,
      );
      const selectedMakeName =
        selectedMakeObj?.name ||
        getSpreadsheetValue(selectedMakeFromSheet, "Make Name");

      if (isSalam) {
        console.log("[Salam Vehicle Data] Resolving selected make", {
          selectedMakeId,
          foundMake: selectedMakeObj || selectedMakeFromSheet,
          availableMakesCount: makeArray.length,
        });
      }

      if (!selectedMakeName) {
        setVehicleModel([]);
        return;
      }

      const searchMakeName = normalizeVehicleName(selectedMakeName);

      console.log("[Model Extraction] Selected make:", {
        id: selectedMakeId,
        name: selectedMakeName,
        searchTerm: searchMakeName,
      });

      // Try spreadsheet first
      if (makeModelMapping.data.length > 0) {
        const matchingRows = makeModelMapping.data.filter((row) => {
          const rowMakeName = normalizeVehicleName(
            getSpreadsheetValue(row, "Make Name"),
          );
          const rowMakeId = getSpreadsheetValue(row, "Make ID");

          return (
            rowMakeName === searchMakeName ||
            rowMakeName.includes(searchMakeName) ||
            searchMakeName.includes(rowMakeName) ||
            rowMakeId === selectedMakeId
          );
        });

        console.log(
          `[Model Extraction] Found ${matchingRows.length} matching rows in spreadsheet`,
        );

        if (matchingRows.length > 0) {
          const models = matchingRows.map((row) => {
            const modelId = getSpreadsheetValue(row, "Model ID");
            const modelName = getSpreadsheetValue(row, "Model Name");

            if (renewData.insuranceType === "NICON" || isSalam) {
              return {
                id: modelId,
                name: modelName,
              };
            } else {
              return {
                vehicleModelId: modelId,
                modelName: modelName,
              };
            }
          });

          setVehicleModel(models);
          return;
        }
      }

      // Fallback: Fetch from API if spreadsheet didn't load or returned no models
      console.log(
        "[Model Extraction] Spreadsheet data empty or no match, trying API fallback...",
        {
          spreadsheetRows: makeModelMapping.data.length,
          spreadsheetLoading: makeModelMapping.loading,
          spreadsheetLastFetched: makeModelMapping.lastFetched,
          spreadsheetError: makeModelMapping.error,
          selectedMakeId,
          selectedMakeName,
        },
      );

      const fetchFromApi = async () => {
        try {
          if (selectedMakeId) {
            setIsLoading(true);
            const apiRes = await (renewData.insuranceType === "NICON"
              ? FixedAgent.getNiconVehicleModels({
                  id: selectedMakeId,
                  token,
                })
              : isSalam
                ? FixedAgent.getSalamVehicleModels({
                    id: selectedMakeId,
                    token,
                  })
              : FixedAgent.getVehicleModelsByID({
                  id: selectedMakeId,
                  token,
                }));

            const apiModels =
              renewData.insuranceType === "NICON"
                ? apiRes.data.data.model
                : isSalam
                  ? apiRes.data.data || []
                : apiRes.data.modelToReturn || [];

            if (Array.isArray(apiModels)) {
              if (isSalam) {
                console.log("[Salam Vehicle Data] Model API success", {
                  selectedMakeId,
                  modelsCount: apiModels.length,
                  sampleModel: apiModels[0],
                });
              }
              console.log(
                `[Model Extraction] Fetched ${apiModels.length} models from API fallback`,
              );
              setVehicleModel(apiModels);
            }
            setIsLoading(false);
          }
        } catch (err) {
          console.error("[Model Extraction] API fallback failed:", err);
          setIsLoading(false);
          showErrorToast("Failed to fetch vehicle models");
        }
      };

      fetchFromApi();
    };

    extractModelsFromSpreadsheet();
  }, [
    selectedMakeId,
    renewData.insuranceType,
    makeModelMapping.data,
    makeModelMapping.error,
    makeModelMapping.lastFetched,
    makeModelMapping.loading,
    niconMake,
    ceiMake,
    salamMeta,
    agMeta,
    isAg,
    isAgFamily,
    isSalam,
    token,
  ]);

  /**
   * Initialize vehicle colors on mount
   */
  useEffect(() => {
    // Colors are already initialized from constants, no need to fetch
    console.log("[Vehicle Colors] Initialized from constants");
  }, []);

  return {
    vehicleColor,
    vehicleModel,
    isLoading,
    yearRange,
  };
};
